import { useEffect, useId, useState, type ReactNode } from 'react';
import { Link, useRouterState } from '@tanstack/react-router';
import { Building2, ChevronDown, ChevronRight, Users } from 'lucide-react';
import { cn } from '@rudi/ui/lib/utils';
import { usePendingTenants, useTenants, useTenantTeams } from '../api/hooks';

const linkClass =
  'flex min-w-0 flex-1 items-center gap-2 rounded-3xl px-3 py-2 text-sm text-muted-foreground hover:bg-accent hover:text-accent-foreground';
const activeProps = { className: 'bg-accent font-medium text-accent-foreground' };

function Branch({
  path,
  label,
  icon,
  collapsed = false,
  onNavigate,
  children,
}: {
  path: string;
  label: string;
  icon: ReactNode;
  collapsed?: boolean;
  onNavigate: () => void;
  children: ReactNode;
}) {
  const pathname = useRouterState({ select: state => state.location.pathname });
  const active = pathname === path || pathname.startsWith(`${path}/`);
  const [expanded, setExpanded] = useState(active);
  const id = useId();
  useEffect(() => {
    if (active) setExpanded(true);
  }, [active, pathname]);

  return (
    <div>
      <div
        className={cn(
          'flex items-center rounded-3xl text-muted-foreground hover:bg-accent hover:text-accent-foreground',
          pathname === path && 'bg-accent font-medium text-accent-foreground'
        )}
      >
        <Link
          to={path}
          onClick={onNavigate}
          className={cn(
            'flex min-w-0 flex-1 items-center gap-2 rounded-3xl px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
            collapsed && 'md:justify-center md:px-0'
          )}
          activeOptions={{ exact: true }}
          title={label}
        >
          {icon}
          <span className={cn('truncate', collapsed && 'md:hidden')}>{label}</span>
        </Link>
        <button
          type="button"
          aria-label={`${expanded ? 'Collapse' : 'Expand'} ${label}`}
          aria-expanded={expanded}
          aria-controls={id}
          onClick={() => setExpanded(value => !value)}
          className={cn(
            'shrink-0 rounded-3xl p-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
            collapsed && 'md:hidden'
          )}
        >
          {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        </button>
      </div>
      <div
        id={id}
        hidden={!expanded}
        className={cn('ml-3 space-y-1 border-l pl-2', collapsed && 'md:hidden')}
      >
        {expanded && children}
      </div>
    </div>
  );
}

function LoadState({
  loading,
  error,
  retry,
}: {
  loading: boolean;
  error: unknown;
  retry: () => void;
}) {
  if (loading) return <p className="px-3 py-2 text-xs text-muted-foreground">Loading…</p>;
  if (error)
    return (
      <div className="px-3 py-2 text-xs" role="alert">
        Could not load navigation.
        <button type="button" onClick={retry} className="ml-1 underline">
          Retry
        </button>
      </div>
    );
  return null;
}

function TeamLinks({ tenantId, onNavigate }: { tenantId: string; onNavigate: () => void }) {
  const teams = useTenantTeams(tenantId);
  return (
    <>
      <LoadState loading={teams.isLoading} error={teams.error} retry={() => void teams.refetch()} />
      {!teams.isLoading && !teams.error && teams.data?.length === 0 && (
        <p className="px-3 py-2 text-xs text-muted-foreground">No teams yet.</p>
      )}
      {teams.data?.map(team => (
        <Link
          key={team.id}
          to="/tenants/$tenantId/teams/$teamId"
          params={{ tenantId, teamId: team.id }}
          onClick={onNavigate}
          className={linkClass}
          activeOptions={{ exact: true }}
          activeProps={activeProps}
          title={team.displayName}
        >
          <span className="truncate">{team.displayName}</span>
          {team.status !== 'active' && <span className="text-xs">Archived</span>}
        </Link>
      ))}
    </>
  );
}

function TenantBranch({
  tenantId,
  name,
  collapsed,
  onNavigate,
}: {
  tenantId: string;
  name: string;
  collapsed?: boolean;
  onNavigate: () => void;
}) {
  return (
    <Branch
      path={`/tenants/${tenantId}`}
      label={name}
      collapsed={collapsed}
      icon={<Building2 className="h-4 w-4 shrink-0" />}
      onNavigate={onNavigate}
    >
      <Branch
        path={`/tenants/${tenantId}/teams`}
        label="Teams"
        icon={<Users className="h-4 w-4 shrink-0" />}
        onNavigate={onNavigate}
      >
        <TeamLinks tenantId={tenantId} onNavigate={onNavigate} />
      </Branch>
      <Link
        to="/tenants/$tenantId/access"
        params={{ tenantId }}
        onClick={onNavigate}
        className={linkClass}
        activeOptions={{ exact: true }}
        activeProps={activeProps}
      >
        Roles and permissions
      </Link>
    </Branch>
  );
}

function TenantDirectory({ onNavigate }: { onNavigate: () => void }) {
  const tenants = useTenants();
  const pending = usePendingTenants(false);
  return (
    <>
      <LoadState
        loading={tenants.isLoading}
        error={tenants.error}
        retry={() => void tenants.refetch()}
      />
      {tenants.data?.tenants.map(tenant => (
        <TenantBranch
          key={tenant.id}
          tenantId={tenant.id}
          name={tenant.name}
          onNavigate={onNavigate}
        />
      ))}
      <LoadState
        loading={pending.isLoading}
        error={pending.error}
        retry={() => void pending.refetch()}
      />
      {pending.data?.pending_tenants.map(tenant => (
        <Link
          key={tenant.id}
          to="/tenants"
          onClick={onNavigate}
          className={linkClass}
          title={`${tenant.name}: prepare this tenant from the overview`}
        >
          <span className="truncate">{tenant.name}</span>
          <span className="text-xs">Pending</span>
        </Link>
      ))}
    </>
  );
}

export function TenantNavigation({
  collapsed,
  onNavigate,
}: {
  collapsed: boolean;
  onNavigate: () => void;
}) {
  return (
    <Branch
      path="/tenants"
      label="Tenants"
      icon={<Building2 className="h-4 w-4 shrink-0" />}
      collapsed={collapsed}
      onNavigate={onNavigate}
    >
      <TenantDirectory onNavigate={onNavigate} />
    </Branch>
  );
}
